import { useState } from 'react';
import { Button, ErrorNote, Field, Input, Select } from './ui';
import { RECURRING_PERIODS, VOLUME_UNITS, api, type Deal, type RecurringPeriod, type VolumeUnit } from '../lib/api';
import { dateInputValue, dateOnly, moneyIn } from '../lib/format';
import { useAction } from '../lib/hooks';

/** Periods per year, for the annualised figure shown beside a recurring amount. */
const PERIODS_PER_YEAR: Record<RecurringPeriod, number> = { month: 12, quarter: 4, year: 1 };

/** True when the deal carries any retainer term at all. */
export function hasRetainer(deal: Deal): boolean {
  return Boolean(
    deal.recurring_amount || deal.included_volume || deal.term_start || deal.term_end || deal.renewal_date,
  );
}

/**
 * One-line summary of a deal's retainer, for lists: "$5,000 / month · renews Jan 5, 2027".
 * Empty when it has none.
 */
export function retainerSummary(deal: Deal): string {
  const parts: string[] = [];
  if (deal.recurring_amount && deal.recurring_period) {
    parts.push(`${moneyIn(deal.recurring_amount, deal.currency)} / ${deal.recurring_period}`);
  }
  if (deal.renewal_date) parts.push(`renews ${dateOnly(deal.renewal_date)}`);
  return parts.join(' · ');
}

/**
 * The retainer terms on a deal: what recurs, what is included, the term and the
 * renewal date. Read-only until Edit is pressed. The annualised figure is shown
 * for comparison only; it is not added to the pipeline value, which stays
 * whatever was typed in the deal's own Value field.
 */
export function DealRetainerSection({ deal, onChanged }: { deal: Deal; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState('');
  const [period, setPeriod] = useState<RecurringPeriod>('month');
  const [volume, setVolume] = useState('');
  const [unit, setUnit] = useState<VolumeUnit>('words');
  const [termStart, setTermStart] = useState('');
  const [termEnd, setTermEnd] = useState('');
  const [renewal, setRenewal] = useState('');

  function startEditing() {
    setAmount(deal.recurring_amount ? String(Number(deal.recurring_amount)) : '');
    setPeriod(deal.recurring_period ?? 'month');
    setVolume(deal.included_volume ? String(deal.included_volume) : '');
    setUnit(deal.included_volume_unit ?? 'words');
    setTermStart(dateInputValue(deal.term_start));
    setTermEnd(dateInputValue(deal.term_end));
    setRenewal(dateInputValue(deal.renewal_date));
    setEditing(true);
  }

  const save = useAction(async () => {
    await api.put(`/api/deals/${deal.id}/retainer`, {
      recurring_amount: amount ? Number(amount) : null,
      recurring_period: amount ? period : null,
      included_volume: volume ? Number(volume) : null,
      included_volume_unit: volume ? unit : null,
      term_start: termStart || null,
      term_end: termEnd || null,
      renewal_date: renewal || null,
    });
    setEditing(false);
    onChanged();
  });

  const annual =
    deal.recurring_amount && deal.recurring_period
      ? Number(deal.recurring_amount) * PERIODS_PER_YEAR[deal.recurring_period]
      : null;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">Retainer</h3>
        {!editing && (
          <Button size="sm" variant="ghost" onClick={startEditing}>
            {hasRetainer(deal) ? 'Edit' : 'Add terms'}
          </Button>
        )}
      </div>

      {editing ? (
        <div className="grid gap-3">
          {save.error && <ErrorNote message={save.error} />}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={`Recurring amount (${deal.currency})`} hint="Leave blank for a project.">
              <div className="flex gap-2">
                <Input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="5000" />
                <Select value={period} onChange={(e) => setPeriod(e.target.value as RecurringPeriod)} className="w-auto" aria-label="Period">
                  {RECURRING_PERIODS.map((p) => (
                    <option key={p} value={p}>
                      per {p}
                    </option>
                  ))}
                </Select>
              </div>
            </Field>
            <Field label="Included volume" hint="Per term, not per period.">
              <div className="flex gap-2">
                <Input type="number" min="1" value={volume} onChange={(e) => setVolume(e.target.value)} placeholder="100000" />
                <Select value={unit} onChange={(e) => setUnit(e.target.value as VolumeUnit)} className="w-auto" aria-label="Unit">
                  {VOLUME_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </Select>
              </div>
            </Field>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Term start">
              <Input type="date" value={termStart} onChange={(e) => setTermStart(e.target.value)} />
            </Field>
            <Field label="Term end">
              <Input type="date" value={termEnd} onChange={(e) => setTermEnd(e.target.value)} />
            </Field>
            <Field label="Renewal date" hint="A won deal reaches Today 60 days before.">
              <Input type="date" value={renewal} onChange={(e) => setRenewal(e.target.value)} />
            </Field>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setEditing(false)}>Cancel</Button>
            <Button variant="primary" loading={save.pending} onClick={() => void save.run()}>
              Save terms
            </Button>
          </div>
        </div>
      ) : hasRetainer(deal) ? (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-xs text-ink-500">Recurring</dt>
            <dd className="text-ink-800">
              {deal.recurring_amount && deal.recurring_period
                ? `${moneyIn(deal.recurring_amount, deal.currency)} / ${deal.recurring_period}`
                : '—'}
              {annual !== null && deal.recurring_period !== 'year' && (
                <span className="block text-xs text-ink-400">≈ {moneyIn(annual, deal.currency)} / year</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-ink-500">Included</dt>
            <dd className="text-ink-800">
              {deal.included_volume ? `${deal.included_volume.toLocaleString('en-US')} ${deal.included_volume_unit}` : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-ink-500">Term</dt>
            <dd className="text-ink-800">
              {deal.term_start || deal.term_end ? `${dateOnly(deal.term_start)} → ${dateOnly(deal.term_end)}` : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-ink-500">Renews</dt>
            <dd className="text-ink-800">{dateOnly(deal.renewal_date)}</dd>
          </div>
        </dl>
      ) : (
        <p className="text-sm text-ink-500">No retainer terms. This reads as a one-off project.</p>
      )}
    </div>
  );
}
